import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, ArrowUpDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/api";
import { formatINR, RiskBadge, initials, riskStyle } from "@/lib/risk";

const FILTERS = ["All", "High Risk", "Watch", "Healthy", "Recovered"];

export default function RiskQueue() {
  const [students, setStudents] = useState([]);
  const [filter, setFilter] = useState("All");
  const [q, setQ] = useState("");
  const nav = useNavigate();

  useEffect(() => { api.students().then(setStudents).catch(() => {}); }, []);

  const counts = useMemo(() => {
    const c = { All: students.length };
    FILTERS.slice(1).forEach((f) => (c[f] = students.filter((s) => s.risk_level === f).length));
    return c;
  }, [students]);

  const rows = useMemo(() => {
    let r = students;
    if (filter !== "All") r = r.filter((s) => s.risk_level === filter);
    if (q.trim()) r = r.filter((s) => s.name.toLowerCase().includes(q.toLowerCase()));
    return [...r].sort((a, b) => b.risk_score - a.risk_score);
  }, [students, filter, q]);

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div>
        <h1 className="font-display text-3xl font-bold text-ink">Risk Queue</h1>
        <p className="text-ink-muted">Students sorted by retention urgency — highest risk first.</p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f.toLowerCase().replace(/\s/g, "-")}`}
              className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-all ${
                filter === f ? "bg-ink text-white" : "border border-border bg-white text-ink-muted hover:text-ink"
              }`}
            >
              {f} <span className="opacity-60">· {counts[f] ?? 0}</span>
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
          <Input data-testid="risk-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search students…" className="h-10 rounded-xl pl-9" />
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden overflow-hidden rounded-2xl border border-border bg-white card-shadow lg:block">
        <table className="w-full text-sm" data-testid="risk-table">
          <thead>
            <tr className="border-b border-border bg-secondary/50 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
              <th className="px-5 py-3.5">Student</th>
              <th className="px-4 py-3.5"><span className="inline-flex items-center gap-1">Risk <ArrowUpDown className="h-3 w-3" /></span></th>
              <th className="px-4 py-3.5">Level</th>
              <th className="px-4 py-3.5">Primary Signal</th>
              <th className="px-4 py-3.5">Monthly Fee</th>
              <th className="px-4 py-3.5">Last Contact</th>
              <th className="px-4 py-3.5">Status</th>
              <th className="px-4 py-3.5"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => {
              const rc = riskStyle(s.risk_level);
              return (
                <tr key={s.id} onClick={() => nav(`/students/${s.id}`)} className="cursor-pointer border-b border-border last:border-0 transition-colors hover:bg-secondary/40"
                  data-testid={`risk-row-${s.id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-xs font-bold text-ink">{initials(s.name)}</span>
                      <div>
                        <div className="font-semibold text-ink">{s.name}</div>
                        <div className="text-xs text-ink-muted">{s.grade} · {s.course}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><span className="font-display text-lg font-bold" style={{ color: rc.color }}>{s.risk_score}</span></td>
                  <td className="px-4 py-3"><RiskBadge level={s.risk_level} size="sm" /></td>
                  <td className="px-4 py-3 text-ink-soft">{s.primary_signal}</td>
                  <td className="px-4 py-3 font-semibold text-ink">{formatINR(s.monthly_fee)}</td>
                  <td className="px-4 py-3 text-ink-muted">{s.last_contact_days}d ago</td>
                  <td className="px-4 py-3 text-ink-soft">{s.status_label}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => nav(`/students/${s.id}`)}
                      data-testid={`view-${s.id}`} className="gap-1 text-indigo-brand hover:text-indigo-deep">
                      View <ArrowRight className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {rows.length === 0 && <div className="py-12 text-center text-ink-muted">No students match this filter.</div>}
      </div>

      {/* Mobile cards */}
      <div className="space-y-3 lg:hidden">
        {rows.map((s) => {
          const rc = riskStyle(s.risk_level);
          return (
            <button key={s.id} onClick={() => nav(`/students/${s.id}`)}
              data-testid={`risk-card-${s.id}`}
              className="w-full rounded-2xl border border-border bg-white p-4 text-left card-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-xs font-bold text-ink">{initials(s.name)}</span>
                  <div>
                    <div className="font-semibold text-ink">{s.name}</div>
                    <div className="text-xs text-ink-muted">{s.grade} · {s.course}</div>
                  </div>
                </div>
                <span className="font-display text-2xl font-bold" style={{ color: rc.color }}>{s.risk_score}</span>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <RiskBadge level={s.risk_level} size="sm" />
                <span className="text-sm font-semibold text-ink">{formatINR(s.monthly_fee)}/mo</span>
              </div>
              <div className="mt-2 text-xs text-ink-muted">{s.primary_signal} · {s.status_label}</div>
            </button>
          );
        })}
        {rows.length === 0 && <div className="py-12 text-center text-ink-muted">No students match this filter.</div>}
      </div>
    </div>
  );
}
